import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Params } from '@angular/router'

import { appConstants } from '../../../app.constants'
import { IncentiveMode } from '../../../shared/enums/incentive-mode.enum'
import { Country } from '../../../shared/interfaces/resources/country.interface'
import { Field } from '../../../shared/interfaces/resources/field.interface'
import { Theme } from '../../../shared/interfaces/resources/theme.interface'
import { Aggregation } from '../../../shared/interfaces/visualizations/aggregation.interface'
import { BreadcrumbService } from '../../../shared/services/breadcrumb.service'
import { CountryService } from '../../../shared/services/country.service'
import { IncentiveService } from '../../../shared/services/incentive.service'
import { MetaService } from '../../../shared/services/meta.service'
import { ThemeService } from '../../../shared/services/theme.service'

@Component({
  selector: 'app-country-detail',
  templateUrl: './country-detail.component.html',
  styleUrls: ['./country-detail.component.scss']
})
export class CountryDetailComponent implements OnInit {
  country: Country
  countries: Country[]
  fieldLabels: { [key: string]: string }
  incentiveModeCountryView: IncentiveMode = IncentiveMode.CountryView

  loadingDownload: boolean = false
  isPolicyChangesExtended: boolean = true

  selectedAggregation: Aggregation
  cyprusNote: string = appConstants.cyprusNote

  constructor(
    private activatedRoute: ActivatedRoute,
    private countryService: CountryService,
    private themeService: ThemeService,
    private metaService: MetaService,
    private incentiveService: IncentiveService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.countryService.show(params['code']).then((countryRes: Country) => {
        this.country = countryRes

        // Select first aggregation for each group.
        this.country.aggregationGroups.forEach((group) => {
          group.selectedAggregation = group.aggregations[0]
        })

        this.themeService.list().then((themeRes) => {
          const policyChangesFields = themeRes.find(
            (theme: Theme) => theme.id === 'policyChanges'
          ).fields

          // Format the fields of PolicyChanges in API to an Object that value can be displayed in template.
          this.fieldLabels = policyChangesFields.reduce((obj, field: Field) => {
            return {
              ...obj,
              [field.id]: field.label
            }
          }, {})
        })

        this.countryService.list().then((countryRes: Country[]) => {
          this.countries = countryRes.filter(
            (comparativeCountry) => comparativeCountry.id !== this.country.id
          )
        })

        this.breadcrumbService.breadcrumbLinks.next([
          { label: 'Countries', path: '/countries' },
          { label: this.country.label }
        ])

        this.metaService.setDynamicTags({
          title: `${this.country.label} - Details`,
          description: `Detailed summary of the R&D tax incentives currently and historically implemented in ${this.country.label}. Additional quantitative data on the generosity of ${this.country.label}'s R&D support policies. `,
          path: `countries/${this.country.id}`
        })
      })
    })
  }

  async downloadIncentives(incentiveIds: string[]): Promise<void> {
    this.loadingDownload = true
    await this.incentiveService.download(incentiveIds)
    this.loadingDownload = false
  }

  /**
   * Group policy changes by incentiveLabel and year.
   * @param policyChanges Array of policy changes.
   * @returns Grouped policy changes.
   */
  groupPolicyChangesByIncentive(policyChanges: any[]): any[] {
    const grouped: any[] = []
    const incentiveMap = new Map()

    // Group by incentiveLabel
    for (const change of policyChanges) {
      if (!incentiveMap.has(change.incentiveLabel)) {
        incentiveMap.set(change.incentiveLabel, {
          incentiveLabel: change.incentiveLabel,
          years: []
        })
      }
      const incentiveGroup = incentiveMap.get(change.incentiveLabel)

      // Find or create year group
      let yearGroup = incentiveGroup.years.find(
        (y: any) => y.year === change.year
      )
      if (!yearGroup) {
        yearGroup = { year: change.year, fields: [] }
        incentiveGroup.years.push(yearGroup)
      }
      yearGroup.fields.push(change.fields)
    }

    // Convert Map to array for template iteration
    incentiveMap.forEach((value) => grouped.push(value))
    return grouped
  }
}
